import React, { useLayoutEffect, useRef, useState } from 'react';
import { prefersReducedMotion } from '../../utils/subscriptionAccessibility';

export interface LearnerSlideModalPanelProps {
  visible: boolean;
  panelClassName: string;
  children: React.ReactNode;
  panelRef?: React.RefObject<HTMLDivElement | null>;
  onTransitionEnd?: (e: React.TransitionEvent<HTMLDivElement>) => void;
  ariaLabelledBy?: string;
  /** Outgoing panel during phase hand-off (slides down under the incoming panel). */
  stackRole?: 'solo' | 'outgoing' | 'incoming';
}

export const LearnerSlideModalPanel: React.FC<LearnerSlideModalPanelProps> = ({
  visible,
  panelClassName,
  children,
  panelRef,
  onTransitionEnd,
  ariaLabelledBy,
  stackRole = 'solo',
}) => {
  const localRef = useRef<HTMLDivElement | null>(null);
  const [shown, setShown] = useState(false);

  const setRefs = (node: HTMLDivElement | null) => {
    localRef.current = node;
    if (panelRef) {
      (panelRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
    }
  };

  useLayoutEffect(() => {
    if (!visible) {
      setShown(false);
      return undefined;
    }

    if (prefersReducedMotion()) {
      setShown(true);
      return undefined;
    }

    setShown(false);
    const el = localRef.current;
    if (el) void el.offsetHeight;

    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setShown(true));
    });

    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, [visible]);

  const stackClass =
    stackRole === 'outgoing'
      ? 'pointer-events-none absolute inset-0 z-10'
      : stackRole === 'incoming'
        ? 'relative z-20'
        : 'relative';

  return (
    <div
      ref={setRefs}
      role="dialog"
      aria-modal="true"
      {...(ariaLabelledBy ? { 'aria-labelledby': ariaLabelledBy } : {})}
      className={`keyboard-slide-modal-panel keyboard-slide-modal-panel--slide-up pointer-events-auto flex flex-col overflow-hidden rounded-2xl shadow-2xl${
        shown ? ' is-visible' : ''
      } ${stackClass} ${panelClassName}`}
      onTransitionEnd={onTransitionEnd}
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </div>
  );
};

export default LearnerSlideModalPanel;
